import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Link,
  Progress,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiAlertTriangle, FiCheckCircle, FiExternalLink } from "react-icons/fi";
import {
  BuyerBidV2EspdAnswerDto,
  EspdResponseDataType,
  type BuyerBidV2EspdCategoryDto,
  type BuyerBidV2EspdRequirementDto,
  type BuyerBidV2EspdRequirementGroupDto,
  type BuyerBidV2EspdRequirementValueDto,
} from "../../../../autogen/bff-api";

export type Tone = "ok" | "issue" | "neutral";

export const toneBg = (tone: Tone) => {
  if (tone === "issue") return "smStatusIssueBg";
  if (tone === "neutral") return "smStatusNeutralBg";
  return "smStatusOkBg";
};

export const toneText = (tone: Tone) => {
  if (tone === "issue") return "smStatusIssueText";
  if (tone === "neutral") return "smStatusNeutralText";
  return "smStatusOkText";
};

export const toneBorder = (tone: Tone) => {
  if (tone === "issue") return "smStatusIssueBorder";
  if (tone === "neutral") return "smStatusNeutralBorder";
  return "smStatusOkBorder";
};

export const toneAccent = (tone: Tone) => {
  if (tone === "issue") return "smStatusIssueAccent";
  if (tone === "neutral") return "smStatusNeutralAccent";
  return "smStatusOkAccent";
};

export const StatusPill = ({ tone, children }: { tone: Tone; children: ReactNode }) => (
  <Badge
    bg={toneBg(tone)}
    color={toneText(tone)}
    border="1px solid"
    borderColor={toneBorder(tone)}
    px="2.5"
    py="1"
    borderRadius="full"
    textTransform="none"
    fontWeight="bold"
    whiteSpace="nowrap"
  >
    <HStack spacing="1.5">
      {tone === "ok" && <Icon as={FiCheckCircle} boxSize="3" />}
      <Text as="span">{children}</Text>
    </HStack>
  </Badge>
);

export const EspdEmptyState = () => {
  const { t } = useTranslation();
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="2"
      py="16"
      rounded="xl"
      bg="smBackground"
      border="1px dashed"
      borderColor="smBorder"
      color="smMuted"
    >
      <Icon as={FiAlertTriangle} boxSize="6" />
      <Text fontWeight="bold" color="smText">
        {t("No ESPD response")}
      </Text>
      <Text fontSize="sm">{t("The supplier has not submitted an ESPD response for this bid.")}</Text>
    </Flex>
  );
};

export const RequirementGroup = ({ group }: { group: BuyerBidV2EspdRequirementGroupDto }) => {
  const { t } = useTranslation();
  const showInstanceLabel = group.repeatable || group.instances.length > 1;

  return (
    <Stack spacing="3">
      {group.instances.map((instance, index) => (
        <Box
          key={`${group.id}-${instance.instanceIndex ?? index}`}
          pt={showInstanceLabel ? "3" : "0"}
          borderTop={showInstanceLabel && index > 0 ? "1px solid" : undefined}
          borderColor="smBorder"
        >
          {showInstanceLabel && (
            <Text fontSize="xs" fontWeight="bold" color="smMuted" textTransform="uppercase" mb="3">
              {t("Response {{number}}", { number: index + 1 })}
            </Text>
          )}
          <Stack spacing="0" divider={<Box borderTop="1px solid" borderColor="smBorder" />}>
            {instance.requirements.map((requirement) => (
              <RequirementRow key={`${requirement.id}-${instance.instanceIndex ?? "single"}`} requirement={requirement} />
            ))}
            {instance.subGroups.map((subGroup) => (
              <RequirementGroup key={subGroup.id} group={subGroup} />
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

const RequirementRow = ({ requirement }: { requirement: BuyerBidV2EspdRequirementDto }) => (
  <LocalizedRequirementRow requirement={requirement} />
);

const LocalizedRequirementRow = ({ requirement }: { requirement: BuyerBidV2EspdRequirementDto }) => {
  const { t, i18n } = useTranslation();
  const label = buyerFacingRequirementLabel(
    localizedText(i18n.language, requirement.labelSource, requirement.labelTranslation, requirement.label),
    t,
  );

  return (
    <Box py="3">
      <Text fontSize="sm" fontWeight="semibold" color="smMuted" lineHeight="short" overflowWrap="anywhere">
        {label}
      </Text>
      <Box minW="0" mt="1">
        <RequirementValue requirement={requirement} />
      </Box>
    </Box>
  );
};

const RequirementValue = ({ requirement }: { requirement: BuyerBidV2EspdRequirementDto }) => {
  const { t, i18n } = useTranslation();
  const value = requirement.value;

  if (!value || isEmptyValue(value)) {
    return <MutedValue>{t("Not answered")}</MutedValue>;
  }

  switch (requirement.responseDataType) {
    case EspdResponseDataType.Indicator:
      return <IndicatorValue value={value.indicator} />;
    case EspdResponseDataType.Description:
      return value.description ? (
        <Text color="smText" lineHeight="tall" whiteSpace="pre-wrap" maxW="82ch">
          {value.description}
        </Text>
      ) : (
        <MutedValue>{t("Not answered")}</MutedValue>
      );
    case EspdResponseDataType.Date:
      return <MetricValue value={value.date ? formatDate(value.date, i18n.language) : null} />;
    case EspdResponseDataType.Amount:
      return <MetricValue value={formatAmount(value.amount, value.currencyId, i18n.language)} mono />;
    case EspdResponseDataType.CodeCountry:
      return <MetricValue value={formatCountryCode(value.code, i18n.language)} />;
    case EspdResponseDataType.Code:
      return <MetricValue value={value.code ?? null} />;
    case EspdResponseDataType.Percentage:
      return <PercentageValue value={value.percentage} />;
    case EspdResponseDataType.Period:
      return <MetricValue value={formatPeriod(value.period, i18n.language)} />;
    case EspdResponseDataType.QuantityInteger:
    case EspdResponseDataType.Quantity:
      return <MetricValue value={formatQuantity(value.quantity, value.quantityUnitCode, i18n.language)} />;
    case EspdResponseDataType.QuantityYear:
      return <MetricValue value={formatYear(value.quantity, i18n.language)} mono />;
    case EspdResponseDataType.EvidenceUrl:
      return <EvidenceValue value={value} />;
    default:
      return <FallbackValue value={value} />;
  }
};

const IndicatorValue = ({ value }: { value?: boolean | null }) => {
  const { t } = useTranslation();
  if (value == null) return <MutedValue>{t("Not answered")}</MutedValue>;
  return <StatusPill tone="neutral">{value ? t("Yes") : t("No")}</StatusPill>;
};

const MetricValue = ({ value, mono = false }: { value?: string | null; mono?: boolean }) => {
  const { t } = useTranslation();
  if (!value) return <MutedValue>{t("Not answered")}</MutedValue>;
  return (
    <Text color="smText" fontWeight="semibold" fontFamily={mono ? "mono" : undefined}>
      {value}
    </Text>
  );
};

const PercentageValue = ({ value }: { value?: number | null }) => {
  const { t, i18n } = useTranslation();
  if (value == null) return <MutedValue>{t("Not answered")}</MutedValue>;
  const bounded = Math.max(0, Math.min(100, value));
  return (
    <Box maxW="220px">
      <Text color="smText" fontWeight="semibold">
        {`${formatNumber(value, i18n.language)} %`}
      </Text>
      <Progress mt="2" value={bounded} size="xs" colorScheme="teal" borderRadius="full" />
    </Box>
  );
};

const EvidenceValue = ({ value }: { value: BuyerBidV2EspdRequirementValueDto }) => {
  const { t } = useTranslation();
  const safeHref = safeExternalHref(value.evidenceUrl);

  if (!value.evidenceUrl) {
    if (value.evidenceName) {
      return (
        <Text color="smText" fontWeight="semibold" overflowWrap="anywhere">
          {value.evidenceName}
        </Text>
      );
    }
    return <MutedValue>{t("Not answered")}</MutedValue>;
  }

  if (!safeHref) {
    return (
      <Text color="smText" fontWeight="semibold" overflowWrap="anywhere">
        {value.evidenceName || value.evidenceUrl}
      </Text>
    );
  }

  return (
    <Link href={safeHref} isExternal color="smPrimary" fontWeight="semibold">
      <HStack as="span" spacing="1.5" align="center">
        <Text as="span" overflowWrap="anywhere">
          {value.evidenceName || formatEvidenceUrl(value.evidenceUrl)}
        </Text>
        <Icon as={FiExternalLink} boxSize="3.5" />
      </HStack>
    </Link>
  );
};

const FallbackValue = ({ value }: { value: BuyerBidV2EspdRequirementValueDto }) => {
  const { t, i18n } = useTranslation();
  const candidates = [
    value.description,
    value.date,
    formatAmount(value.amount, value.currencyId, i18n.language),
    value.code,
    value.percentage != null ? `${formatNumber(value.percentage, i18n.language)} %` : null,
    formatQuantity(value.quantity, value.quantityUnitCode, i18n.language),
    value.period,
    value.evidenceName,
    value.evidenceUrl,
  ].filter(Boolean);

  if (candidates.length === 0) {
    return <MutedValue>{t("Not answered")}</MutedValue>;
  }

  return (
    <Stack spacing="1">
      {candidates.map((candidate) => (
        <Text key={candidate} color="smText" fontWeight="semibold">
          {candidate}
        </Text>
      ))}
    </Stack>
  );
};

export const MutedValue = ({ children }: { children: ReactNode }) => (
  <Text color="smMuted" fontStyle="italic">
    {children}
  </Text>
);

export const localizedText = (
  language: string,
  source?: string | null,
  translation?: string | null,
  fallback?: string | null,
) => {
  const isNorwegian = localeFor(language).startsWith("nb");
  const preferred = isNorwegian ? translation : source;
  return preferred?.trim() || fallback || "";
};

const buyerFacingRequirementLabel = (label: string, t: TFunction) => {
  const normalized = label.trim().replace(/[?:]+$/, "").toLocaleLowerCase();
  if (normalized === "your answer" || normalized === "ditt svar") {
    return t("Supplier's answer");
  }
  return label;
};

export const localeFor = (language: string) => {
  const normalized = language?.toLowerCase();
  return normalized?.startsWith("nb") || normalized?.startsWith("no") ? "nb-NO" : "en-GB";
};

export const safeId = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, "_");

// Keep category keys visible to i18next-parser:
// t("espd_category_EXCLUSION_CONVICTIONS") t("espd_category_EXCLUSION_CONTRIBUTIONS")
// t("espd_category_EXCLUSION_SOCIAL") t("espd_category_EXCLUSION_BUSINESS")
// t("espd_category_EXCLUSION_MISCONDUCT") t("espd_category_EXCLUSION_CONFLICT")
// t("espd_category_EXCLUSION_NATIONAL")
const CATEGORY_LABEL_KEYS: Record<string, string> = {
  EXCLUSION_CONVICTIONS: "espd_category_EXCLUSION_CONVICTIONS",
  EXCLUSION_CONTRIBUTIONS: "espd_category_EXCLUSION_CONTRIBUTIONS",
  EXCLUSION_SOCIAL: "espd_category_EXCLUSION_SOCIAL",
  EXCLUSION_BUSINESS: "espd_category_EXCLUSION_BUSINESS",
  EXCLUSION_MISCONDUCT: "espd_category_EXCLUSION_MISCONDUCT",
  EXCLUSION_CONFLICT: "espd_category_EXCLUSION_CONFLICT",
  EXCLUSION_NATIONAL: "espd_category_EXCLUSION_NATIONAL",
};

export const espdCategoryName = (category: BuyerBidV2EspdCategoryDto, t: TFunction) => {
  const key = CATEGORY_LABEL_KEYS[category.code];
  return key ? t(key) : category.name;
};

export const espdStatus = (
  deviationCount: number,
  unansweredCount: number,
  t: (value: string, options?: { count: number }) => string,
): { label: string; tone: Tone } => {
  if (deviationCount > 0) {
    return { label: t("espd_deviation_count", { count: deviationCount }), tone: "issue" };
  }
  if (unansweredCount > 0) {
    return { label: t("espd_unanswered_count", { count: unansweredCount }), tone: "neutral" };
  }
  return { label: t("OK"), tone: "ok" };
};

export const espdCategoryHasIssue = (category: BuyerBidV2EspdCategoryDto) =>
  category.criteria.some(
    (criterion) =>
      criterion.answer === BuyerBidV2EspdAnswerDto.Deviation ||
      criterion.answer === BuyerBidV2EspdAnswerDto.Unanswered,
  );


const isEmptyValue = (value: BuyerBidV2EspdRequirementValueDto) =>
  value.indicator == null &&
  !value.description &&
  !value.date &&
  value.amount == null &&
  !value.currencyId &&
  !value.code &&
  value.percentage == null &&
  value.quantity == null &&
  !value.quantityUnitCode &&
  !value.period &&
  !value.evidenceUrl &&
  !value.evidenceName;

const formatDate = (value: string, language: string) => {
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(localeFor(language), { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(
    date,
  );
};

const formatPeriod = (value: string | null | undefined, language: string) => {
  if (!value) return null;
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})\s*(?:\/|\s+-\s+)\s*(\d{4}-\d{2}-\d{2})$/);
  if (!match) return value;
  const [, start, end] = match;
  return `${formatDate(start, language)} - ${formatDate(end, language)}`;
};

const formatAmount = (amount?: number | null, currencyId?: string | null, language = "en") => {
  if (amount == null) return null;
  return currencyId ? `${formatNumber(amount, language)} ${currencyId}` : formatNumber(amount, language);
};

const formatCountryCode = (code?: string | null, language = "en") => {
  if (!code) return null;
  const normalizedCode = code.toUpperCase();
  try {
    const displayNames = new Intl.DisplayNames([localeFor(language)], { type: "region" });
    const countryName = displayNames.of(normalizedCode);
    return countryName ? `${normalizedCode} · ${countryName}` : code;
  } catch {
    return code;
  }
};

const formatQuantity = (quantity?: number | null, unit?: string | null, language = "en") => {
  if (quantity == null) return null;
  return unit ? `${formatNumber(quantity, language)} ${formatUnitCode(unit, quantity, language)}` : formatNumber(quantity, language);
};

const formatUnitCode = (unit: string, quantity: number, language = "en") => {
  const normalizedUnit = unit.toUpperCase();
  const isNorwegian = localeFor(language).startsWith("nb");
  const unitLabels: Record<string, { en: [string, string]; no: [string, string] }> = {
    C62: { en: ["unit", "units"], no: ["stk.", "stk."] },
    DAY: { en: ["day", "days"], no: ["dag", "dager"] },
    HUR: { en: ["hour", "hours"], no: ["time", "timer"] },
    KGM: { en: ["kg", "kg"], no: ["kg", "kg"] },
    MON: { en: ["month", "months"], no: ["måned", "måneder"] },
  };
  const labels = unitLabels[normalizedUnit];
  if (!labels) return unit;
  const [singular, plural] = isNorwegian ? labels.no : labels.en;
  return quantity === 1 ? singular : plural;
};

const formatEvidenceUrl = (value?: string | null) => {
  if (!value) return "";
  try {
    const url = new URL(value);
    return `${url.host}${url.pathname}`.replace(/\/$/, "");
  } catch {
    return value;
  }
};

const safeExternalHref = (value?: string | null) => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
};

const formatYear = (quantity: number | null | undefined, language = "en") => {
  if (quantity == null) return null;
  return new Intl.NumberFormat(localeFor(language), { maximumFractionDigits: 0, useGrouping: false }).format(quantity);
};

const formatNumber = (value: number, language = "en") =>
  new Intl.NumberFormat(localeFor(language), { maximumFractionDigits: 2 }).format(value).replace(/\u00a0/g, " ");
